import JSEncryption, { JSEncryptionInterface } from "./Encryption/JSEncryption";
import Ports from "./Ports";
import PluginClientProvider, { PluginResponse } from "./Providers/base_provider";
import FetchProvider from "./Providers/fetch_provider";
import AliveData from "./Routes/AliveData";
import CCCardData from "./Routes/CCCardData";
import SignData from "./Routes/SignData";
import forge from 'node-forge';
import { Buffer } from 'buffer';

export enum Algorithms {
    "SHA1" = "SHA1",
    // "SHA256" = "SHA256" TODO: to be supported in future
}

export enum CardType {
    "CC" = "CC",
    "OM" = "OM"
}

export type PluginClientType = {
    provider: PluginClientProvider;
    algorithm: Algorithms,
    port?: number,
    type?: CardType
    baseUrl: string,
    encryption: JSEncryptionInterface,
    getCardData(addressPin?: string): Promise<PluginResponse<CCCardData>>,
    sign(data: string, pin?: string): Promise<PluginResponse<SignData>>,
    isAlive(port: number): Promise<PluginResponse<AliveData>>,
    getPort(): Promise<number | void>,
    parseEndpoint(endpoint: string): Promise<string>
};

const keyChain = JSEncryption;

const PluginClient: PluginClientType = {
    baseUrl: "http://127.0.0.1",
    provider: FetchProvider,
    algorithm: Algorithms.SHA1,
    type: undefined,
    encryption: keyChain,
    async getCardData(addressPin?: string) {
        keyChain.loadKeyChain();
        const url = await this.parseEndpoint("/api/getInfoCC");
        const configs = {
            method: "POST",
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                addressPin: addressPin || "",
                certificate: this.encryption.keychain.public
            })
        };

        const response: Awaited<PluginResponse<CCCardData>> = await this.provider.doRequest<CCCardData>(url, configs).then((response: PluginResponse<CCCardData>): PluginResponse<CCCardData> => {
            if (response.key && response.data) {
                const parsedKey: Record<string, string> = JSON.parse(this.encryption.decrypt(Buffer.from(response.key, this.encryption.encoding).toString('binary')));
                const iv: string = Buffer.from(parsedKey.iv, 'hex').toString('binary');
                const key: string = Buffer.from(parsedKey.key, 'hex').toString('binary');
                const buffered: forge.util.ByteStringBuffer = forge.util.createBuffer(Buffer.from(response.data as unknown as string, "hex").toString('binary'));

                // decrypt some bytes using CBC mode
                const cipher = forge.cipher.createDecipher("AES-CBC", key);
                cipher.start({ iv: iv });
                cipher.update(buffered);
                cipher.finish();

                response.data = JSON.parse(cipher.output.toString());
            }

            return response;
        });

        return response;
    },
    async sign(data, pin?: string) {
        const url = await this.parseEndpoint("/api/sign");
        const configs = {
            method: "POST",
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                type: this.type || "",
                data: data,
                pin: pin || "",
                algorithm: this.algorithm
            })
        };
        return this.provider.doRequest(url, configs);
    },
    async isAlive(port) {
        const url = this.baseUrl + ":" + port + "/api/checkState"
        const configs = {
            method: "GET",
            signal: AbortSignal.timeout(500)
        }

        return this.provider.doRequest<AliveData>(url, configs);
    },
    async getPort(): Promise<number | void> {
        if (this.port) {
            return this.port;
        }

        let success = false;
        const portPromise: Promise<PluginResponse<AliveData>>[] = [];

        for (const prt of Ports) {
            portPromise.push(
                // eslint-disable-next-line no-async-promise-executor
                new Promise(async (resolve, reject) => {
                    return await this.isAlive(prt).then((resp) => {
                        if (resp.data) {
                            resp.data.port = prt;
                        }
                        resolve(resp);
                    }).catch((err) => {
                        reject(err);
                    });
                }));
        }

       return await Promise.any(portPromise)
            .then((resp: PluginResponse<AliveData>) => {
                success = resp?.data?.alive || false;
                if (success) {
                    this.port = resp?.data?.port;
                    return this.port;
                }
            }).finally(() => {
                if (!success) {
                    throw new Error("All ports are blocked");
                }
            });
    },
    async parseEndpoint(endpoint: string) {
        const port = await this.getPort();
        return this.baseUrl + ":" + port + endpoint
    }
};

export default PluginClient;
